import { cloneDeep, get } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import { TextMeasurer } from '@/utils/text-kit';
import {
    LayerType,
    MaterialIdsType,
    TextWritingType,
    type ILayerProps,
    type IVectorLayerProps,
    type VectorTextInfo,
} from '@/version-types';
import { pushHistory, updateLayer } from '../utils';

export interface IFontBase {
    /**
     * 设置字体
     * @param layerID - 图层 ID
     * @param fontFamily - 字体名称
     * @param materialID - 字体素材 ID
     * @returns
     */
    setFontFamily(layerID: string, fontFamily: string, materialID: string): Promise<ILayerProps | void>;

    /**
     * 设置字号
     * @param layerID - 图层 ID
     * @param fontSize - 字号
     * @returns
     */
    setFontSize(layerID: string, fontSize: number, options?: { writeHistory?: boolean }): ILayerProps | void;

    /**
     * 设置字体 italic (font-style)
     * @param layerID - 图层 ID
     * @param flag - 是否设置 italic
     * @returns
     */
    setItalicStyle(layerID: string, flag: boolean): ILayerProps | void;

    /**
     * 设置字体 bold (font-weight)
     * @param layerID - 图层 ID
     * @param flag - 是否设置 bold
     * @returns
     */
    setBoldStyle(layerID: string, flag: boolean): ILayerProps | void;
}

export class FontBase implements IFontBase {
    private prevLayer: ILayerProps | null = null;

    constructor(private framework: IFramework) {}

    private updateMaterialIDs(layer: ILayerProps, materialID: string) {
        const prevMaterialIDMap = layer.materialIds || {};
        const nextFontFamilyMaterialIDs = [materialID];

        const nextMaterialIDMap = cloneDeep(prevMaterialIDMap);
        nextMaterialIDMap[MaterialIdsType.FONT_FAMILY] = nextFontFamilyMaterialIDs;

        return nextMaterialIDMap;
    }

    private calcLayoutCauseByFontFamily(layer: ILayerProps) {
        const { width: layerWidth, height: layerHeight } = layer;
        const textInfo = get(layer, 'data.textInfo') as VectorTextInfo;

        const { writingMode } = textInfo;
        const isHorizontal = writingMode === TextWritingType.HORIZONTAL_TB;

        const textSize = TextMeasurer.measure(layer, {
            keepConstantWidth: isHorizontal,
            keepConstantHeight: !isHorizontal,
            preservePadding: textInfo.padding,
        });
        const nextLayerWidth = textSize.width > layerWidth ? textSize.width : layerWidth;
        const nextLayerHeight = textSize.height > layerHeight ? textSize.height : layerHeight;

        return {
            width: nextLayerWidth,
            height: nextLayerHeight,
        };
    }

    async setFontFamily(layerID: string, fontFamily: string, materialID: string) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return;

        const nextTextInfo: VectorTextInfo = { ...textInfo, fontFamily };
        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        // ATTENTION: 该方法会改变 nextLayer 的 runtime
        await this.framework.registerFont(nextLayer, fontFamily, true);

        const { width: nextLayerWidth, height: nextLayerHeight } = this.calcLayoutCauseByFontFamily(nextLayer);
        nextLayer.width = nextLayerWidth;
        nextLayer.height = nextLayerHeight;

        nextLayer.materialIds = this.updateMaterialIDs(nextLayer, materialID);

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].family);

        return nextLayer;
    }

    private calcLayoutCauseByFontSize(layer: ILayerProps) {
        const { width: layerWidth, height: layerHeight } = layer;
        const textInfo = get(layer, 'data.textInfo') as VectorTextInfo;

        const { writingMode, fontSize } = textInfo;
        const isHorizontal = writingMode === TextWritingType.HORIZONTAL_TB;

        const textSize = TextMeasurer.measure(layer, {
            keepConstantWidth: isHorizontal && layerWidth > fontSize,
            keepConstantHeight: !isHorizontal,
            preservePadding: textInfo.padding,
        });
        const scale = fontSize < 12 ? fontSize / 12 : 1;

        const tempWidth = textSize.width * scale;
        const tempHeight = textSize.height * scale;

        const proactiveWidth = layer.__proactiveWidth || layerWidth;
        const proactiveHeight = layer.__proactiveHeight || layerHeight;

        const nextLayerWidth = tempWidth <= proactiveWidth ? proactiveWidth : tempWidth;
        const nextLayerHeight = tempHeight <= proactiveHeight ? proactiveHeight : tempHeight;

        return {
            width: nextLayerWidth,
            height: nextLayerHeight,
        };
    }

    setFontSize(layerID: string, fontSize: number, options = { writeHistory: true }) {
        const prevLayer = (() => {
            if (this.prevLayer) return this.prevLayer;
            this.prevLayer = this.framework.getLayer(layerID, true);
            return this.prevLayer;
        })();

        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return;

        const nextTextInfo: VectorTextInfo = { ...textInfo, fontSize };
        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { width: nextLayerWidth, height: nextLayerHeight } = this.calcLayoutCauseByFontSize(nextLayer);
        nextLayer.width = nextLayerWidth;
        nextLayer.height = nextLayerHeight;

        const { writeHistory } = options;

        updateLayer(this.framework, nextLayer);
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].size);
            this.prevLayer = null;
        }

        return nextLayer;
    }

    private calcLayoutCauseByFontStyle(layer: ILayerProps) {
        const { width: layerWidth, height: layerHeight } = layer;
        const textInfo = get(layer, 'data.textInfo') as VectorTextInfo;

        const { writingMode } = textInfo;
        const isHorizontal = writingMode === TextWritingType.HORIZONTAL_TB;

        const textSize = TextMeasurer.measure(layer, {
            keepConstantWidth: isHorizontal,
            keepConstantHeight: !isHorizontal,
            preservePadding: textInfo.padding,
        });
        const nextLayerWidth = Math.max(layerWidth, textSize.width);
        const nextLayerHeight = Math.max(layerHeight, textSize.height);

        return {
            width: nextLayerWidth,
            height: nextLayerHeight,
        };
    }

    private setFontStyle(layerID: string, kind: 'italic' | 'bold', flag: boolean) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return;

        const nextTextInfo: VectorTextInfo = { ...textInfo, [kind]: flag };
        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { width: nextLayerWidth, height: nextLayerHeight } = this.calcLayoutCauseByFontStyle(nextLayer);
        nextLayer.width = nextLayerWidth;
        nextLayer.height = nextLayerHeight;

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);

        return nextLayer;
    }

    setItalicStyle(layerID: string, flag: boolean) {
        return this.setFontStyle(layerID, 'italic', flag);
    }

    setBoldStyle(layerID: string, flag: boolean) {
        return this.setFontStyle(layerID, 'bold', flag);
    }
}
